import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RestApiService, SKIP_ERROR_SNACKBAR } from '@ccfx/shared-lib';
import { map, Observable } from 'rxjs';
import {
  CustomerDocumentPolicyDto,
  DocumentType,
  DocumentUploadResponseDto,
  ExpiringDocumentsResponseDto,
  KycReviewStatusDto,
  OnboardingApplication,
  OnboardingApplicationDetail,
  RegisterDocumentRequestDto,
  SubmitOnboardingResponseDto,
} from '../models/onboarding-models';

/**
 * Maps the UI-level DocumentType keys to the backend's allowed document type strings.
 * The backend validates against a fixed set; this mapping bridges the two vocabularies.
 */
const DOCUMENT_TYPE_TO_API: Record<DocumentType, string> = {
  businessLicense: 'IncorporationCertificate',
  assessment: 'Other',
  schedule50: 'OwnershipDocument',
  msbLicense: 'Other',
  orgChart: 'DirectorRegistry',
  financialStatements: 'BankingDocument',
  foreignBranches: 'OwnershipDocument',
  amlPolicy: 'Other',
  sanctionsPolicy: 'Other',
  antiBriberyPolicy: 'Other',
  regulatoryRegistration: 'Other',
  wolfsbergQuestionnaire: 'Other',
  effectivenessReviewRecentYear: 'Other',
  effectivenessReviewPreviousYear: 'Other',
  restrictedIndustriesList: 'Other',
  additionalDocument1: 'Other',
  additionalDocument2: 'Other',
};

@Injectable({ providedIn: 'root' })
export class OnboardingApiService {
  private readonly restApi = inject(RestApiService);

  /**
   * Save the onboarding application as a draft (can be called repeatedly while partner is Pending).
   * Pass silent=true to suppress the global error toast (e.g. for background auto-saves).
   * POST /api/onboarding/save
   */
  saveApplication(
    payload: OnboardingApplication,
    silent = false,
  ): Observable<SubmitOnboardingResponseDto> {
    const context = silent ? new HttpContext().set(SKIP_ERROR_SNACKBAR, true) : undefined;
    return this.restApi.post<SubmitOnboardingResponseDto>('/api/onboarding/save', payload, {
      context,
    });
  }

  /**
   * Submit the completed onboarding application for KYC review.
   * Transitions company status to PendingKYC.
   * POST /api/onboarding/submit
   */
  submitForReview(payload: OnboardingApplication): Observable<SubmitOnboardingResponseDto> {
    return this.restApi.post<SubmitOnboardingResponseDto>('/api/onboarding/submit', payload);
  }

  /**
   * @deprecated Use submitForReview() instead.
   * Submit the complete onboarding application.
   * POST /api/onboarding/apply
   */
  submitApplication(payload: OnboardingApplication): Observable<SubmitOnboardingResponseDto> {
    return this.restApi.post<SubmitOnboardingResponseDto>('/api/onboarding/apply', payload);
  }

  /**
   * Get the current (latest) onboarding application for the authenticated company.
   * Returns a 404 when no application exists — call with silent=true to suppress
   * the global error toast in that case.
   * GET /api/onboarding/current
   */
  getCurrentApplication(silent = false): Observable<OnboardingApplicationDetail> {
    const context = silent ? new HttpContext().set(SKIP_ERROR_SNACKBAR, true) : undefined;
    return this.restApi.get<OnboardingApplicationDetail>('/api/onboarding/current', { context });
  }

  /**
   * Get the full history of onboarding applications for the authenticated company.
   * The API serializes each entity in full, so every item carries its `rawPayloadJson`.
   * GET /api/onboarding/history
   */
  getApplicationHistory(silent = false): Observable<OnboardingApplicationDetail[]> {
    const context = silent ? new HttpContext().set(SKIP_ERROR_SNACKBAR, true) : undefined;
    return this.restApi.get<OnboardingApplicationDetail[]>('/api/onboarding/history', { context });
  }

  /**
   * Get the document upload policy (allowed types, max size, etc.).
   * GET /api/onboarding/documents/policy
   */
  getDocumentPolicy(): Observable<CustomerDocumentPolicyDto> {
    return this.restApi.get<CustomerDocumentPolicyDto>('/api/onboarding/documents/policy');
  }

  /**
   * Upload a document file via multipart form.
   * POST /api/onboarding/documents/upload
   * Angular's HttpClient automatically sets the correct Content-Type with boundary for FormData.
   */
  uploadDocument(
    file: File,
    documentType: string,
    applicationId?: number,
    replacesDocumentId?: number,
    expiresAt?: string,
  ): Observable<{ fileRef: string; fileName: string; documentId: number }> {
    const formData = new FormData();
    formData.append('File', file, file.name);
    formData.append(
      'DocumentType',
      DOCUMENT_TYPE_TO_API[documentType as DocumentType] ?? documentType,
    );
    formData.append('DocumentScope', 'Company');
    if (applicationId != null) {
      formData.append('ApplicationId', applicationId.toString());
    }
    if (replacesDocumentId != null) {
      formData.append('ReplacesDocumentId', replacesDocumentId.toString());
    }
    if (expiresAt) {
      formData.append('ExpiresAt', expiresAt);
    }

    return this.restApi
      .post<DocumentUploadResponseDto>('/api/onboarding/documents/upload', formData)
      .pipe(
        map((res) => ({ fileRef: res.blobName, fileName: file.name, documentId: res.documentId })),
      );
  }

  registerDocument(request: RegisterDocumentRequestDto): Observable<DocumentUploadResponseDto> {
    return this.restApi.post<DocumentUploadResponseDto>(
      '/api/onboarding/documents/register',
      request,
    );
  }

  getExpiringDocuments(withinDays = 30): Observable<ExpiringDocumentsResponseDto> {
    return this.restApi.get<ExpiringDocumentsResponseDto>(
      `/api/onboarding/documents/expiring?withinDays=${withinDays}`,
    );
  }

  /**
   * Check if a business registration number is available (not already in use by another company).
   * GET /api/onboarding/business-registration/{registrationNumber}/availability
   */
  checkBusinessRegistration(registrationNumber: string): Observable<{ available: boolean }> {
    return this.restApi.get<{ available: boolean }>(
      `/api/onboarding/business-registration/${encodeURIComponent(registrationNumber)}/availability`,
    );
  }

  /**
   * Whether the company's KYC is approaching/past its re-review date, and whether a
   * re-review is already in progress. Drives the FR-7 portal banner and notifications.
   * GET /api/onboarding/kyc-review/status
   */
  getKycReviewStatus(silent = false): Observable<KycReviewStatusDto> {
    const context = silent ? new HttpContext().set(SKIP_ERROR_SNACKBAR, true) : undefined;
    return this.restApi.get<KycReviewStatusDto>('/api/onboarding/kyc-review/status', { context });
  }

  /**
   * Self-service: start a new KYC re-review application ahead of/around the re-review date.
   * POST /api/onboarding/kyc-review/start
   */
  startKycReReview(): Observable<SubmitOnboardingResponseDto> {
    return this.restApi.post<SubmitOnboardingResponseDto>('/api/onboarding/kyc-review/start', {});
  }
}
